// © 2025 Kaitlyn Troxell
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { supabase, isSupabaseConfigured } from '../superbaseClient';
import LoadingSpinner from './LoadingSpinner';
import PageHeader from './PageHeader';

function FileManager({ user, userRole, onNavigate, currentProject, projects, onProjectChange }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    category: 'documents',
    description: '',
    file: null
  });
  const fileInputRef = React.useRef(null);

  const isAdmin = userRole === 'admin';
  const isHomeowner = userRole === 'homeowner';

  // Define categories - filter for homeowners
  const allCategories = [
    { id: 'all', name: 'All Files', icon: '📁' },
    { id: 'documents', name: 'Documents', icon: '📄' },
    { id: 'images', name: 'Images', icon: '🖼️' },
    { id: 'plans', name: 'Plans & Drawings', icon: '📐' },
    { id: 'contracts', name: 'Contracts', icon: '📝' },
    { id: 'invoices', name: 'Invoices', icon: '💰' },
    { id: 'photos', name: 'Progress Photos', icon: '📸' },
    { id: 'other', name: 'Other', icon: '📦' }
  ];

  // Homeowner-visible categories
  const homeownerCategories = [
    { id: 'all', name: 'All Files', icon: '📁' },
    { id: 'documents', name: 'Documents', icon: '📄' },
    { id: 'images', name: 'Images', icon: '🖼️' },
    { id: 'plans', name: 'Plans & Drawings', icon: '📐' },
    { id: 'photos', name: 'Progress Photos', icon: '📸' },
    { id: 'contracts', name: 'Contracts', icon: '📝' },
    { id: 'other', name: 'Other', icon: '📦' }
  ];

  const categories = isHomeowner ? homeownerCategories : allCategories;

  // Load files from Firebase
  useEffect(() => {
    if (!currentProject?.id) return;

    const loadFiles = async () => {
      setLoading(true);
      try {
        const filesRef = collection(db, 'project_files');
        const q = query(
          filesRef,
          where('project_id', '==', currentProject.id),
          orderBy('created_at', 'desc')
        );
        const snapshot = await getDocs(q);
        
        const filesData = [];
        snapshot.forEach(doc => {
          const fileData = {
            id: doc.id,
            ...doc.data()
          };
          
          // If homeowner, only show files in homeowner-visible categories
          if (isHomeowner) {
            const homeownerCategoryIds = homeownerCategories.map(c => c.id);
            if (homeownerCategoryIds.includes(fileData.category)) {
              filesData.push(fileData);
            }
          } else {
            filesData.push(fileData);
          }
        });
        
        setFiles(filesData);
      } catch (error) {
        console.error('Error loading files:', error);
        alert('Failed to load files');
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, [currentProject?.id, isHomeowner]);

  // Handle file upload (admin only)
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!isAdmin) {
      alert('Only administrators can upload files');
      return;
    }
    
    if (!isSupabaseConfigured()) {
      alert('Supabase is not configured. Please add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your .env file.');
      return;
    }
    
    if (!uploadForm.file) {
      alert('Please select a file');
      return;
    }

    if (!currentProject?.id) {
      alert('No project selected');
      return;
    }

    setLoading(true);

    try {
      const file = uploadForm.file;
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${currentProject.id}/${uploadForm.category}/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Supabase upload error:', uploadError);
        throw uploadError;
      }

      // Get signed URL for private bucket (expires in 1 year)
      const { data: signedUrlData, error: urlError } = await supabase.storage
        .from('project-files')
        .createSignedUrl(filePath, 31536000); // 1 year in seconds

      if (urlError) {
        console.error('Error creating signed URL:', urlError);
        throw urlError;
      }

      const fileUrl = signedUrlData.signedUrl;

      // Save metadata to Firebase
      const fileMetadata = {
        project_id: currentProject.id,
        category: uploadForm.category,
        description: uploadForm.description,
        original_name: file.name,
        file_name: fileName,
        file_path: filePath,
        file_url: fileUrl,
        file_size: file.size,
        file_type: file.type,
        uploaded_by: user.uid,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'project_files'), fileMetadata);

      setFiles(prev => [{
        id: docRef.id,
        ...fileMetadata,
        created_at: new Date()
      }, ...prev]);

      // Reset form
      setUploadForm({
        category: 'documents',
        description: '',
        file: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setShowUploadModal(false);
      
      alert('File uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(`Failed to upload file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle file delete (admin only)
  const handleDeleteFile = async (fileId, filePath) => {
    if (!isAdmin) {
      alert('Only administrators can delete files');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this file?')) return;

    if (!isSupabaseConfigured()) {
      alert('Supabase is not configured.');
      return;
    }

    setLoading(true);
    try {
      // Delete from Supabase Storage
      const { error: storageError } = await supabase.storage
        .from('project-files')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete metadata from Firebase
      await deleteDoc(doc(db, 'project_files', fileId));

      setFiles(prev => prev.filter(file => file.id !== fileId));
      
      alert('File deleted successfully!');
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Failed to delete file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle file download
  const handleDownloadFile = async (filePath, originalName) => {
    if (!isSupabaseConfigured()) {
      alert('Supabase is not configured.');
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from('project-files')
        .download(filePath);

      if (error) throw error;

      // Create download link
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
    if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return '📊';
    if (fileType?.includes('zip') || fileType?.includes('rar')) return '📦';
    return '📄';
  };

  // Check if file can be previewed
  const canPreview = (fileType) => {
    return fileType?.startsWith('image/') || fileType === 'application/pdf';
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Filter files by category
  const filteredFiles = selectedCategory === 'all' 
    ? files 
    : files.filter(file => file.category === selectedCategory);

  return (
    <>
      {loading && <LoadingSpinner message="Processing..." />}
      
      <PageHeader
        currentPage="files"
        onNavigate={onNavigate}
        currentProject={currentProject}
        projects={projects}
        onProjectChange={onProjectChange}
        userRole={userRole}
      />
      
      <div style={{ minHeight: '100vh'}}>
        {/* Main Content - Split Layout */}
        <div className="punch-list-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Sidebar - Categories */}
          <div className="punch-list-sidebar" style={{
            width: '280px',
            backgroundColor: '#fff',
            borderRight: '1px solid #ddd',
            overflowY: 'auto',
            padding: '1.5rem 1rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center'}}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                Categories
              </h3>
              {/* Mobile toggle button */}
              <button 
                className="mobile-menu-btn"
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                style={{ padding: '4px 20px', alignment: 'center', fontSize: '18px' }}
              >
                {mobileCategoriesOpen ? '▲' : '▼'}
              </button>
            </div>
            
            {/* Mobile category list - toggleable */}
            <div style={{ display: mobileCategoriesOpen ? 'block' : 'none' }} className="category-list-mobile">
              {categories.map((category) => {
                const count = category.id === 'all' 
                  ? files.length 
                  : files.filter(f => f.category === category.id).length;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setMobileCategoriesOpen(false);
                    }}
                    className="category-btn-mobile"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      border: selectedCategory === category.id ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: selectedCategory === category.id ? '#e3f2fd' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{category.icon}</span>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: selectedCategory === category.id ? '600' : '400',
                        color: '#333'
                      }}>
                        {category.name}
                      </span>
                    </div>
                    {count > 0 && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        fontWeight: '500',
                        backgroundColor: '#f0f0f0',
                        padding: '2px 8px',
                        borderRadius: '12px'
                      }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Desktop category list - always visible */}
            <div style={{ display: 'block' }} className="category-list-desktop">
              {categories.map((category) => {
                const count = category.id === 'all' 
                  ? files.length 
                  : files.filter(f => f.category === category.id).length;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      border: selectedCategory === category.id ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: selectedCategory === category.id ? '#e3f2fd' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{category.icon}</span>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: selectedCategory === category.id ? '600' : '400',
                        color: '#333'
                      }}>
                        {category.name}
                      </span>
                    </div>
                    {count > 0 && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        fontWeight: '500',
                        backgroundColor: '#f0f0f0',
                        padding: '2px 8px',
                        borderRadius: '12px'
                      }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Content - Files */}
          <div className="punch-list-content" style={{ 
            flex: 1, 
            padding: '2rem',
            overflowY: 'auto'
          }}>
            {/* Supabase Configuration Warning - admin only */}
            {isAdmin && !isSupabaseConfigured() && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '2rem',
                color: '#856404'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '16px' }}>⚠️ Supabase Not Configured</h4>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  To use the file manager, you need to set up Supabase. Add the following to your <code>.env</code> file:
                </p>
                <pre style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '0.5rem', 
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  fontSize: '12px',
                  overflow: 'auto'
                }}>
{`REACT_APP_SUPABASE_URL=your-project-url
REACT_APP_SUPABASE_ANON_KEY=your-anon-key`}
                </pre>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '12px' }}>
                  See FILE_MANAGER_SETUP.md for detailed instructions.
                </p>
              </div>
            )}

            {/* Header with Upload Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <div>
                <h2 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '24px' }}>
                  {categories.find(c => c.id === selectedCategory)?.icon}{' '}
                  {categories.find(c => c.id === selectedCategory)?.name}
                </h2>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''}
                </div>
              </div>
              
              {isAdmin && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="save-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <span>⬆️</span> Upload File
                </button>
              )}
            </div>

            {/* Files Grid */}
            {filteredFiles.length === 0 ? (
              <div style={{
                backgroundColor: '#fff',
                padding: '3rem',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#666'
              }}>
                <p style={{ fontSize: '16px', marginBottom: '1rem' }}>
                  No files in this category yet.
                </p>
                {isAdmin && (
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="save-btn"
                  >
                    Upload Your First File
                  </button>
                )}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '1.5rem'
              }}>
                {filteredFiles.map(file => (
                  <div
                    key={file.id}
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    {/* File Icon/Preview */}
                    <div 
                      onClick={() => canPreview(file.file_type) && setPreviewFile(file)}
                      style={{
                        width: '100%',
                        height: '120px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1rem',
                        fontSize: '48px',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: canPreview(file.file_type) ? 'pointer' : 'default'
                      }}
                      onMouseEnter={(e) => {
                        if (canPreview(file.file_type)) {
                          e.currentTarget.style.opacity = '0.8';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      {file.file_type?.startsWith('image/') ? (
                        <img 
                          src={file.file_url} 
                          alt={file.original_name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '6px'
                          }}
                        />
                      ) : file.file_type === 'application/pdf' ? (
                        <div style={{ 
                          width: '100%', 
                          height: '100%', 
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          gap: '8px',
                          backgroundColor: '#fff'
                        }}>
                          {/* PDF Preview - first page */}
                          <iframe
                            src={`${file.file_url}#view=FitH&toolbar=0&navpanes=0&scrollbar=0&page=1`}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              pointerEvents: 'none',
                              position: 'absolute',
                              top: 0,
                              left: 0
                            }}
                            title={`${file.original_name} preview`}
                          />
                          {/* Overlay to show it's clickable */}
                          <div style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            PDF
                          </div>
                        </div>
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: '8px' 
                        }}>
                          <span style={{ fontSize: '48px' }}>
                            {getFileIcon(file.file_type)}
                          </span>
                          <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>
                            {file.file_type?.split('/')[1]?.toUpperCase() || 'FILE'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* File Info */}
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {file.original_name}
                      </h4>
                      {file.description && (
                        <p style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '13px',
                          color: '#666',
                          lineHeight: '1.4'
                        }}>
                          {file.description}
                        </p>
                      )}
                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span>{formatFileSize(file.file_size)}</span>
                        <span>
                          {file.created_at?.toDate ? 
                            file.created_at.toDate().toLocaleDateString() : 
                            new Date(file.created_at).toLocaleDateString()
                          }
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      paddingTop: '1rem',
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      {canPreview(file.file_type) ? (
                        <button
                          onClick={() => setPreviewFile(file)}
                          className="save-btn"
                          style={{
                            flex: 1,
                            padding: '6px 12px',
                            fontSize: '13px'
                          }}
                        >
                          👁️ Preview
                        </button>
                      ) : (
                        <button
                          onClick={() => window.open(file.file_url, '_blank')}
                          className="save-btn"
                          style={{
                            flex: 1,
                            padding: '6px 12px',
                            fontSize: '13px'
                          }}
                        >
                          View
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadFile(file.file_path, file.original_name)}
                        className="screenshot-btn"
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          fontSize: '13px',
                          backgroundColor: '#17a2b8',
                          borderColor: '#17a2b8'
                        }}
                      >
                        Download
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteFile(file.id, file.file_path)}
                          className="delete-btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: '13px'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal - Admin only */}
      {isAdmin && showUploadModal && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            setShowUploadModal(false);
            setUploadForm({
              category: 'documents',
              description: '',
              file: null
            });
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              maxWidth: '500px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '2rem',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <h2 style={{ marginTop: 0 }}>Upload File</h2>
            
            <form onSubmit={handleFileUpload}>
              <div className="form-group">
                <label className="form-label">Category <span className="required">*</span></label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  className="form-select"
                  required
                >
                  {categories.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">File <span className="required">*</span></label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => {
                    const selectedFile = e.target.files[0];
                    if (selectedFile) {
                      setUploadForm(prev => ({ ...prev, file: selectedFile }));
                    }
                  }}
                  className="form-input"
                  required
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
                {uploadForm.file && (
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '13px', 
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>✔</span>
                    <span>{uploadForm.file.name}</span>
                    <span>({formatFileSize(uploadForm.file.size)})</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Description (Optional)</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="form-input"
                  rows="3"
                  placeholder="Add a description for this file..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadForm({
                      category: 'documents',
                      description: '',
                      file: null
                    });
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="modal-cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-btn"
                  disabled={!uploadForm.file || loading}
                >
                  {loading ? 'Uploading...' : 'Upload File'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div 
          className="modal-overlay" 
          onClick={() => setPreviewFile(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              backgroundColor: '#fff',
              borderRadius: '8px',
              width: '90vw',
              height: '90vh',
              maxWidth: '1200px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Preview Header */}
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
                  {previewFile.original_name}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                  {formatFileSize(previewFile.file_size)} • {previewFile.file_type}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleDownloadFile(previewFile.file_path, previewFile.original_name)}
                  className="screenshot-btn"
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    backgroundColor: '#17a2b8',
                    borderColor: '#17a2b8'
                  }}
                >
                  ⬇️ Download
                </button>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="delete-btn"
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px'
                  }}
                >
                  ✕ Close
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div style={{
              flex: 1,
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {previewFile.file_type?.startsWith('image/') ? (
                <img 
                  src={previewFile.file_url} 
                  alt={previewFile.original_name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : previewFile.file_type === 'application/pdf' ? (
                <iframe
                  src={`${previewFile.file_url}#view=FitH`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title={previewFile.original_name}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FileManager;